import { Box, Paper, styled, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { FC, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Attachment, FormDefinitions, Permissions } from "../api/dtos";
import type { AppColumn, AppScalarValue } from "./tables/AppTable.types";
import StageAttachmentsContext from "../contexts/StageAttachmentsContext";
import usePermissions from "../hooks/auth/usePermissions";
import GridActions from "./GridActions";
import CollapsibleAppTable from "./tables/CollapsibleAppTable";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import { StyledIconButton } from "./StyledButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const localCalendarDate = (value?: string) => {
    if (value === undefined || value === "") {
        return value;
    }

    const date = DateTime.fromISO(value);

    return date.isValid ? (date.toISODate() ?? value) : value;
};

function requiredAttachmentId(row: Attachment): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Attachment is missing its ID.");
    }

    return row.id;
}

export function createStageAttachmentColumns(
    permissions: readonly Permissions[] | undefined,
    projectId: string | undefined,
    stageId: string | undefined,
    attachmentTypeLabel: (value: AppScalarValue) => string,
): AppColumn<Attachment>[] {
    const columns: AppColumn<Attachment>[] = [
        {
            id: "file",
            label: "",
            headerLabel: "",
            kind: "action",
            scalarType: "text",
            accessor: (row) => String(row.file?.id ?? ""),
            width: 50,
            cell: ({ row }) =>
                row.file ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            height: "100%",
                        }}
                    >
                        <StyledIconButton
                            //@ts-expect-error
                            component="a"
                            href={`/forms/file/download/${row.file.id}`}
                            size="small"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </StyledIconButton>
                    </Box>
                ) : null,
        },
        {
            id: "name",
            label: "Name",
            scalarType: "text",
            accessor: (row) => row.name,
            flex: 1,
        },
        {
            id: "fileName",
            label: "File Name",
            scalarType: "text",
            accessor: (row) => row.file?.fileName,
            flex: 1,
        },
        {
            id: "modifiedDate",
            label: "Date Modified",
            scalarType: "date",
            accessor: (row) => localCalendarDate(row.modifiedDate),
            formatter: (value) =>
                typeof value === "string" && DateTime.fromISO(value).isValid
                    ? DateTime.fromISO(value).toFormat("dd/MM/yyyy")
                    : "",
            flex: 1,
        },
        {
            id: "attachmentTypeId",
            label: "Attachment Type",
            scalarType: "lookup",
            accessor: (row) => row.attachmentTypeId,
            lookup: {
                labelFor: (value) => attachmentTypeLabel(value),
            },
            sortAccessor: (row) => attachmentTypeLabel(row.attachmentTypeId),
            sortScalarType: "text",
            flex: 1,
        },
    ];

    if (
        permissions?.includes(Permissions.StageCanEditAttachment) ||
        permissions?.includes(Permissions.StageCanDeleteAttachment)
    ) {
        columns.push({
            id: "actions",
            label: "",
            headerLabel: "",
            kind: "action",
            scalarType: "text",
            accessor: (row) => String(row.id ?? ""),
            width: 100,
            cell: ({ row }) => (
                <GridActions
                    editPermission={Permissions.StageCanEditAttachment}
                    deletePermission={Permissions.StageCanDeleteAttachment}
                    editUrl={`/projects/${projectId}/stages/${stageId}/attachments/edit/${requiredAttachmentId(row)}`}
                    deleteUrl={`/projects/${projectId}/stages/${stageId}/attachments/delete/${requiredAttachmentId(row)}`}
                />
            ),
        });
    }

    return columns;
}

const useAttachmentColumns = () => {
    const { data: permissions } = usePermissions();
    const metadata = useFormMetadata(FormDefinitions.Attachment);
    const { data: lookups, loading: _lookupsLoading } = useLookups(metadata);
    const params = useParams();
    const stageId = params?.stageId;
    const projectId = params?.projectId;

    return useMemo(
        () =>
            createStageAttachmentColumns(permissions, projectId, stageId, (value) =>
                typeof value === "number"
                    ? (GetFieldLookupItem(lookups, "AttachmentTypeId", value)?.value?.toString() ?? String(value))
                    : String(value ?? ""),
            ),
        [lookups, permissions, projectId, stageId],
    );
};

interface IStageAttachmentsProps {
    stageId: number;
}

const StageAttachments: FC<IStageAttachmentsProps> = () => {
    const { data: attachments, loading: attachmentsLoading } = useContext(StageAttachmentsContext);
    const { data: project } = useContext(ProjectContext);
    const { data: stage } = useContext(StageContext);

    const attachmentColumns = useAttachmentColumns();

    const exportMetadata = useMemo(
        () => ({
            exportTitle: `${project?.name} - ${stage?.name} - Attachments`,
            fileName: `${project?.name} - ${stage?.name} - Attachments`,
            includeDateTime: true,
        }),
        [project, stage],
    );

    const theme = useTheme();

    return (
        <StyledPaper>
            <Box
                sx={{
                    marginTop: theme.spacing(2),
                }}
            >
                <CollapsibleAppTable
                    density="compact"
                    startCollapsed={false}
                    title="Attachments"
                    loading={attachmentsLoading}
                    rows={attachments ?? []}
                    columns={attachmentColumns}
                    getRowId={requiredAttachmentId}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={exportMetadata}
                />
            </Box>
        </StyledPaper>
    );
};

export default StageAttachments;
